import { useMemo } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { PermissionLevelEnum } from "@models/PermissionSection";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import useTableSort from "@components/new/Table/useTableSort";
import { CategoryLensChainLtoRow } from "@apiHooks/useHomeCategoryLensChainLtos";
import { StarsCell } from "./CategoryLensLtosChainTableCells";

interface CategoryLensChainLtosTableProps {
    activeMetricId: keyof CategoryLensChainLtoRow;
    tableData: CategoryLensChainLtoRow[];
}

const ROW_HEIGHT = 50;
const HEAD_ROW_HEIGHT = 30;

const CategoryLensChainLtosTable: FCX<CategoryLensChainLtosTableProps> = ({ activeMetricId, tableData }) => {
    const {
        sortedData,
        sortingField,
        sortingDirection,
        getColumnIconNoDefault,
    } = useTableSort<CategoryLensChainLtoRow>(tableData, activeMetricId, 'desc');

    const tableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                title: 'Date',
                Value: ({ index }) => <>{sortedData[index]?.date ? dayjs(sortedData[index].date).format('MMM YYYY') : ''}</>,
                styles: {
                    cell: {
                        flex: '1 1 5%',
                        minWidth: '80px',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Name',
                Value: ({ index }) => {
                    const item = sortedData[index];

                    if (item?.scorePermissionLevel !== PermissionLevelEnum.Full) {
                        return <>{item?.name}</>;
                    }

                    return (
                        <Link
                            to={{ pathname: item?.link }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item?.name}
                        </Link>
                    );
                },
                styles: {
                    cell: {
                        flex: '1 1 35%',
                        minWidth: '140px',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Unbranded PI',
                icon: getColumnIconNoDefault('unbrandedPi', sortingField, sortingDirection),
                Value: ({ index }) => <StarsCell value={sortedData[index]?.unbrandedPi} starsCount={sortedData[index]?.unbrandedPiStar} />,
                styles: {
                    cell: {
                        flex: '1 1 9%',
                        minWidth: '120px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Uniqueness',
                icon: getColumnIconNoDefault('uniqueness', sortingField, sortingDirection),
                Value: ({ index }) => <StarsCell value={sortedData[index]?.uniqueness} starsCount={sortedData[index]?.uniquenessStar} />,
                styles: {
                    cell: {
                        flex: '1 1 9%',
                        minWidth: '90px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Frequency',
                icon: getColumnIconNoDefault('frequency', sortingField, sortingDirection),
                Value: ({ index }) => <StarsCell value={sortedData[index]?.frequency} starsCount={sortedData[index]?.frequencyStar} />,
                styles: {
                    cell: {
                        flex: '1 1 9%',
                        minWidth: '70px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Draw',
                icon: getColumnIconNoDefault('draw', sortingField, sortingDirection),
                Value: ({ index }) => <StarsCell value={sortedData[index]?.draw} starsCount={sortedData[index]?.drawStar} />,
                styles: {
                    cell: {
                        flex: '1 1 9%',
                        minWidth: '70px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'Value',
                icon: getColumnIconNoDefault('value', sortingField, sortingDirection),
                Value: ({ index }) => <StarsCell value={sortedData[index]?.value} starsCount={sortedData[index]?.valueStar} />,
                styles: {
                    cell: {
                        flex: '1 1 9%',
                        minWidth: '70px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'SCORE',
                icon: getColumnIconNoDefault('score', sortingField, sortingDirection),
                Value: ({ index }) => <>{sortedData[index]?.score}</>,
                styles: {
                    cell: {
                        flex: '1 1 6%',
                        minWidth: '70px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        backgroundColor: '#FEF9F4',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
            {
                title: 'VIABILITY',
                Value: ({ index }) => <>{sortedData[index]?.viability}</>,
                styles: {
                    cell: {
                        flex: '1 1 12%',
                        minWidth: '130px',
                        justifyContent: 'center',
                        textAlign: 'center',
                        backgroundColor: '#FEF9F4',
                        padding: '0 8px',
                    },
                    head: {
                        height: `${HEAD_ROW_HEIGHT}px`,
                    }
                },
            },
        ],
        [
            sortedData, sortingField, sortingDirection, getColumnIconNoDefault,
        ],
    );

    return (
        <div className="CategoryLensChainLtos__table">
            <div
                className="CategoryLensChainLtos__table-wrapper"
                style={{ height: ROW_HEIGHT * sortedData.length + HEAD_ROW_HEIGHT }}
            >
                <Table
                    isCopyButtonHidden
                    config={tableConfig}
                    rowsCount={sortedData.length}
                    rowHeight={ROW_HEIGHT}
                    theme="light"
                />
            </div>
        </div>
    );
};

export default CategoryLensChainLtosTable;